import { HttpService } from "@app/core/services/http/http.service";
import { Injectable, WritableSignal, inject, signal } from "@angular/core";
import {
  DocumentsPlusConfiguration,
  DocumentsPlusConfigurationAdapter,
  DocumentsPlusConfigurationIdList,
  DocumentsPlusConfigurationIdMaintenance,
  DocumentsPlusConfigurationListAdapter,
  DocumentsPlusConfigurationByApplicationIdAdapter,
  DocumentsPlusConfigValues,
  DocumentsPlusScannerConfig,
  DocumentsPlusApplicationConfig
} from "@app/app-modules/documents-plus/documents-plus-configuration/documents-plus-configuration.model";
import { DPLUS_URL } from "@app/shared/constants/url.constants";
import { map, tap } from "rxjs";
import { UserSettingsService } from "@app/core/services/user-settings.service";

@Injectable({
  providedIn: "root"
})
export class DocumentsPlusConfigurationService {
  private http = inject(HttpService);
  private userSettingsService = inject(UserSettingsService);
  private _dplusIds = signal<DocumentsPlusConfigurationIdList[]>([]);
  private _dplusConfig = signal<DocumentsPlusConfigValues>(undefined);
  private _appConfig = signal<DocumentsPlusApplicationConfig>(undefined);
  private _scannerConfig = signal<DocumentsPlusScannerConfig>(undefined);

  fetchConfiguration(data: DocumentsPlusConfiguration) {
    const respAdapter = new DocumentsPlusConfigurationAdapter();
    return this.http
      .addDataParams(data)
      .get(DPLUS_URL.APPLICATION_MASTER_MAINTENANCE)
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  updateConfiguration(data: DocumentsPlusConfiguration) {
    const respAdapter = new DocumentsPlusConfigurationAdapter();
    return this.http
      .addDataParams(data)
      .put(DPLUS_URL.APPLICATION_MASTER_MAINTENANCE, "")
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  deleteConfiguration(applicationId: string) {
    const respAdapter = new DocumentsPlusConfigurationAdapter();
    return this.http
      .addDataParams({ applicationId })
      .delete(DPLUS_URL.APPLICATION_MASTER_MAINTENANCE)
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  fetchConfigurationByAppId(applicationId: string) {
    const respAdapter = new DocumentsPlusConfigurationByApplicationIdAdapter();
    return this.http
      .addDataParams({ applicationId })
      .get(DPLUS_URL.APPLICATION_MASTER_CONFIG)
      .pipe(
        map((response: any) => respAdapter.adapt(response)),
        tap((res) => {
          this._dplusConfig.set(res);
          this._appConfig.set(res.appConfiguration);
          this._scannerConfig.set(res.scannerConfiguration);
        })
      );
  }

  fetchConfigurationIdList(applicationId: string) {
    const respAdapter = new DocumentsPlusConfigurationListAdapter();
    return this.http
      .addDataParams({ applicationId })
      .get(DPLUS_URL.APPLICATION_ID_LIST)
      .pipe(
        map((response: any) => respAdapter.adapt(response)),
        tap((res) => {
          this._dplusIds.set(res);
        })
      );
  }

  fetchConfigurationById(data: DocumentsPlusConfigurationIdMaintenance) {
    const respAdapter = new DocumentsPlusConfigurationAdapter();
    return this.http
      .addDataParams(data)
      .get(DPLUS_URL.APPLICATION_ID_LIST_MAINTENANCE)
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  addConfigurationById(data: DocumentsPlusConfigurationIdMaintenance) {
    const respAdapter = new DocumentsPlusConfigurationAdapter();
    return this.http
      .addDataParams(data)
      .post(DPLUS_URL.APPLICATION_ID_LIST_MAINTENANCE, "")
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  updateConfigurationById(data: DocumentsPlusConfigurationIdMaintenance) {
    const respAdapter = new DocumentsPlusConfigurationAdapter();
    return this.http
      .addDataParams(data)
      .put(DPLUS_URL.APPLICATION_ID_LIST_MAINTENANCE, "")
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  deleteConfigurationById(data: DocumentsPlusConfigurationIdList) {
    const respAdapter = new DocumentsPlusConfigurationAdapter();
    return this.http
      .addDataParams(data)
      .delete(DPLUS_URL.APPLICATION_ID_LIST_DELETE)
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  get descrAutoPopulate() {
    return this.userSettingsService?.inbasketSettings?.isDescrAutoPopulate ?? this._dplusConfig().appConfiguration.descrAutoPopulate;
  }

  get dplusIds(): WritableSignal<DocumentsPlusConfigurationIdList[]> {
    return this._dplusIds;
  }

  get dplusConfig(): WritableSignal<DocumentsPlusConfigValues> {
    return this._dplusConfig;
  }

  get appConfig(): WritableSignal<DocumentsPlusApplicationConfig> {
    return this._appConfig;
  }

  get scannerConfig(): WritableSignal<DocumentsPlusScannerConfig> {
    return this._scannerConfig;
  }
}
